import { useRef, useCallback, useEffect, useMemo } from 'react'
import { assign, createMachine } from 'xstate'
import { useMachine } from '@xstate/react'
import { useCreateSchedules } from 'hooks/queries/brandPlans/useCreateSchedules'
import { useGenerateThemePreview } from 'hooks/queries/brandPlans/useGenerateThemePreview'
import { useModals } from 'almanac-ui/Modal'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { CheckBroken } from 'blaze-ui/icons'
import { ApiError } from 'utils/error'
import { generatePath } from 'react-router-dom'
import { BRAND_KITS_PATH } from 'paths'
import dayjs from 'dayjs'
import {
  MachineContext,
  MachineEvent,
  MachineState,
  EventId,
  StateId,
  Campaign,
  GeneratedTheme,
  ContentTypeSettingsMap,
} from './Types'
import { BrandPlan, BrandPlanContents, ContentStrategy, ContentStrategyType } from 'types'
import { buildContentsFromSettings } from './contentTypeDefaultsUtils'
import { useContentPlanSchedules } from '../useContentPlanSchedules'

const createCampaignMachine = createMachine<MachineContext, MachineEvent, MachineState>({
  id: 'createCampaign',
  initial: StateId.StrategyAndContextSelection,
  states: {
    [StateId.StrategyAndContextSelection]: {
      on: {
        [EventId.Next]: [
          {
            target: StateId.ReviewDetails,
            actions: 'seedCampaigns',
          },
        ],
        [EventId.Back]: {
          actions: 'close',
        },
      },
    },
    [StateId.ContentTypeDefaults]: {
      on: {
        [EventId.SetContentTypes]: {
          target: StateId.ReviewDetails,
          actions: assign({
            contentTypeSettings: (_, event) => event.settings ?? null,
            campaign: (ctx, event) => ({
              ...ctx.campaign,
              content_accounts: event.contentAccounts ?? ctx.campaign?.content_accounts,
            }),
          }),
        },
        [EventId.Back]: StateId.ReviewDetails,
      },
    },
    [StateId.ReviewDetails]: {
      entry: 'generateThemes',
      exit: assign({
        transient: (context) => {
          context.transient.abortController.abort()
          return {
            ...context.transient,
            abortController: new AbortController(),
          }
        },
      }),
      on: {
        [EventId.ThemeResolved]: {
          actions: assign({
            campaign: (ctx, event) =>
              ctx.campaign ? { ...ctx.campaign, theme: event.theme, loading: false } : ctx.campaign,
          }),
        },
        [EventId.SetCampaignDetails]: {
          actions: assign({
            campaign: (ctx, event) => (ctx.campaign ? { ...ctx.campaign, ...event.campaign } : ctx.campaign),
          }),
        },
        [EventId.Back]: [
          {
            target: StateId.StrategyAndContextSelection,
          },
        ],
        [EventId.Create]: {
          target: StateId.Creating,
          actions: assign({
            campaign: (_, event) => event.campaign,
            strategyType: (ctx, event) => event.campaign.strategyType ?? ctx.strategyType,
          }),
        },
        [EventId.Error]: {
          target: StateId.StrategyAndContextSelection,
          actions: 'showError',
        },
        [EventId.SetContentTypes]: {
          target: StateId.ContentTypeDefaults,
          actions: assign({
            contentTypeSettings: (ctx, event) => event.settings ?? ctx.contentTypeSettings,
          }),
        },
      },
    },
    [StateId.Creating]: {
      entry: 'createCampaigns',
      on: {
        [EventId.Error]: {
          target: StateId.ReviewDetails,
          actions: 'showError',
        },
      },
    },
  },
})

// Content buckets that post to a single platform directly. The campaign creation flow
// models everything as crossposts, so these are always empty for a campaign — sending
// them explicitly keeps the schedule from inheriting stale brand-plan counts.
const emptyDirectPlatformContents = (): Pick<
  BrandPlanContents,
  | 'facebook_contents'
  | 'linkedin_contents'
  | 'twitter_contents'
  | 'tiktok_contents'
  | 'youtube_contents'
  | 'google_my_business_contents'
> => ({
  facebook_contents: [],
  linkedin_contents: [],
  twitter_contents: [],
  tiktok_contents: [],
  youtube_contents: [],
  google_my_business_contents: [],
})

// A new campaign defaults to the first slot, one strategy-length out, where it does not overlap
// a campaign of the same strategy. Walking forward from tomorrow means only campaigns the new
// one would actually collide with move the default; one parked months out (a leftover from a
// long-range plan) is left alone. Anchoring on the latest campaign on file instead pushed the
// default to the following year ("Fri, Feb 12") for a campaign wanted next week. (BLZ-16474)
export const computeStartDate = (
  loadedSchedules: Array<{ content_strategy_type: string | null; start_date: string; end_date: string }>,
  strategyType: ContentStrategyType,
  strategyLength: number,
  timezone: string,
): { startDate: string; endDate: string; length: number } => {
  const tomorrow = dayjs().tz(timezone).add(1, 'day').startOf('day')
  const lengthDays = strategyLength * 7

  const upcoming = loadedSchedules
    .filter((s) => s.content_strategy_type === strategyType)
    .map((s) => ({ start: dayjs(s.start_date), end: dayjs(s.end_date) }))
    .filter((s) => !s.end.isBefore(tomorrow, 'day'))
    .sort((a, b) => a.start.diff(b.start))

  let startDate = tomorrow.add(strategyLength, 'week')
  for (const campaign of upcoming) {
    const endDate = startDate.add(lengthDays - 1, 'day')
    const overlaps = !campaign.start.isAfter(endDate, 'day') && !campaign.end.isBefore(startDate, 'day')
    if (overlaps) startDate = campaign.end.add(1, 'day')
  }

  const endDate = startDate.add(lengthDays - 1, 'day')

  return {
    startDate: startDate.format('YYYY-MM-DD'),
    endDate: endDate.format('YYYY-MM-DD'),
    length: strategyLength,
  }
}

interface Props {
  brandPlan: BrandPlan
  onSuccess?: () => void
}

export const useCreateCampaignMachine = ({ brandPlan, onSuccess }: Props) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { closeAllModals } = useModals()
  const sendRef = useRef<(event: MachineEvent) => void>(() => {})
  const prefilledThemesRef = useRef<GeneratedTheme | null>(null)

  const { mutateAsync: generateThemePreview } = useGenerateThemePreview({
    brandPlanId: brandPlan.id,
  })
  const { mutate: createSchedules } = useCreateSchedules(
    { brandPlanId: brandPlan.id },
    {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries(['workspaces', workspaceId, 'schedules'])
        closeAllModals()
        onSuccess?.()
        const count = data.length
        showToastMessage(
          `${count} new campaign${count === 1 ? '' : 's'} generated!`,
          'success',
          undefined,
          5,
          <CheckBroken size={22} />,
          'dark',
        )
      },
      onError: (error) => {
        if (error instanceof ApiError && error.errorCode === 'no_brand_kit_media') {
          const brandKitPath = generatePath(BRAND_KITS_PATH, { workspaceId }) + '#images_videos'
          showToastMessage(
            'Your content preferences require brand kit media, but none are uploaded.',
            'error',
            { type: 'link', to: brandKitPath, label: 'Go to Brand Kit', variant: 'proceed' },
            10,
          )
        } else {
          showErrorMessageToast('There was a problem creating your campaigns. Please try again.')
        }
        sendRef.current({ type: EventId.Error, error: error as Error })
      },
    },
  )

  const { data: schedulesData } = useContentPlanSchedules()
  const loadedSchedules = useMemo(
    () => schedulesData?.pages.flatMap((page) => page.data.schedules) ?? [],
    [schedulesData],
  )

  const buildScheduleParams = useCallback(
    (
      campaign: Campaign,
      strategyType: ContentStrategyType,
      config: ContentStrategy,
      contentTypeSettings: ContentTypeSettingsMap | null,
    ) => {
      const contentsOverride = contentTypeSettings ? buildContentsFromSettings(contentTypeSettings) : null

      return {
        target_link: campaign.target_link,
        content_preferences: campaign.content_preferences,
        start_date: campaign.startDate,
        content_strategy_type: strategyType,
        length: campaign.length,
        week_days: campaign.weekDays,
        daily_frequency: brandPlan.daily_frequency,
        days_before_review: brandPlan.days_before_review ?? 2,
        goal: brandPlan.goal ?? undefined,
        regenerate_themes: false,
        tactics: config.tactics,
        theme: (({ goal_alignment, content_brief, seasonal_tie_in, strategic_role, ...rest }) => rest)(campaign.theme),
        themes: [
          (({ goal_alignment, content_brief, seasonal_tie_in, strategic_role, ...rest }) => rest)(campaign.theme),
        ],
        sources: campaign.sources
          .filter((cs) => cs.roles.includes('source_material'))
          .map((cs) => ({ type: cs.source.type, id: cs.source.id })),
        media_files: campaign.sources
          .filter((cs) => cs.roles.includes('visual_asset') && cs.mediaFileId != null)
          .map((cs) => ({ id: cs.mediaFileId as number })),
        content_accounts: campaign.content_accounts,
        instagram_contents: contentsOverride?.instagram_contents ?? config.instagram_contents,
        crosspost_contents: contentsOverride?.crosspost_contents ?? config.crosspost_contents,
        seo_contents: contentsOverride?.seo_contents ?? config.seo_contents,
        email_contents: contentsOverride?.email_contents ?? config.email_contents,
        video_crosspost_contents: contentsOverride?.video_crosspost_contents ?? config.video_crosspost_contents,
        // The "Content per week" step is the sole source of truth for what this campaign
        // contains, and it only covers the five buckets above. The direct-platform buckets
        // are never shown there and never priced into its credit estimate, so copying the
        // brand plan's values would add posts the user never asked for and wasn't quoted —
        // legacy V1 plans still carry counts here (see BLZ-16231). Platform reach is driven
        // by `tactics` + `content_accounts`, so clearing these does not stop crossposting.
        ...emptyDirectPlatformContents(),
      }
    },
    [brandPlan],
  )

  const machine = useMachine<MachineContext, MachineEvent, MachineState>(createCampaignMachine, {
    context: {
      strategyType: null,
      strategyConfig: null,
      campaign: undefined,
      contentTypeSettings: null,
      themeDetails: null,
      transient: {
        abortController: new AbortController(),
      },
    },
    guards: {
      hasPostContentTypes: () => brandPlan.post_content_types,
    },
    actions: {
      close: () => closeAllModals(),
      showError: (_ctx, event) => {
        if ('error' in event && event.error) {
          showErrorMessageToast(event.error.message || 'Something went wrong. Please try again.')
        }
      },
      seedCampaigns: assign((_ctx, event) => {
        if (event.type !== EventId.Next) return {}

        const strategyChanged = event.strategyType !== _ctx.strategyType
        const themeDetailsChanged = event.themeDetails !== _ctx.themeDetails
        const sourcesChanged =
          event.sources
            .map((s) => s.source.id)
            .sort()
            .join(',') !==
          (_ctx.campaign?.sources ?? [])
            .map((s) => s.source.id)
            .sort()
            .join(',')
        const existingCampaign =
          !strategyChanged && !themeDetailsChanged && !sourcesChanged && _ctx.campaign?.loading === false
            ? _ctx.campaign
            : null
        const existingTheme = existingCampaign?.theme ?? null

        const dates = existingCampaign
          ? {
              startDate: existingCampaign.startDate,
              endDate: existingCampaign.endDate,
              length: existingCampaign.length,
            }
          : computeStartDate(loadedSchedules, event.strategyType, event.strategyConfig.length, brandPlan.timezone)

        return {
          strategyType: event.strategyType,
          strategyConfig: event.strategyConfig,
          contentTypeSettings: strategyChanged ? null : _ctx.contentTypeSettings,
          themeDetails: event.themeDetails,
          campaign: {
            theme: existingTheme ?? { id: `placeholder`, title: '', description: '', type: undefined },
            ...dates,
            weekDays: brandPlan.week_days,
            sources: event.sources,
            loading: existingTheme == null,
            target_link: existingCampaign?.target_link ?? '',
            strategyType: event.strategyType,
            content_preferences: existingCampaign?.content_preferences,
            content_accounts: existingCampaign?.content_accounts,
          },
        }
      }),
      generateThemes: async (ctx) => {
        const abortController = ctx.transient.abortController
        try {
          if (!ctx.strategyType || !ctx.strategyConfig) return
          if (!ctx.campaign?.loading) return

          prefilledThemesRef.current = null
          const strategyType = ctx.strategyType
          const strategyConfig = ctx.strategyConfig
          const campaign = ctx.campaign

          const generatedThemes: Array<{ id: string; description: string }> = []

          if (abortController.signal.aborted) return

          const existingThemes = [...generatedThemes]

          const sourceIds = campaign.sources
            .filter((cs) => cs.roles.includes('source_material'))
            .map((cs) => ({ type: cs.source.type, id: cs.source.id }))

          await generateThemePreview({
            content_strategy_type: strategyType,
            start_date: campaign.startDate,
            length: strategyConfig.length,
            goal: brandPlan.goal ?? undefined,
            existing_themes: existingThemes,
            theme_details: ctx.themeDetails ?? undefined,
            source_ids: sourceIds.length > 0 ? sourceIds : undefined,
          }).then((result) => {
            if (abortController.signal.aborted) return
            generatedThemes.push({
              id: result.theme.id,
              description: result.theme.description,
            })
            prefilledThemesRef.current = result.theme
            sendRef.current({
              type: EventId.ThemeResolved,
              theme: result.theme,
            })
          })
        } catch (error) {
          if (abortController.signal.aborted) return
          sendRef.current({ type: EventId.Error, error: error as Error })
        }
      },
      createCampaigns: (ctx) => {
        if (!ctx.strategyType || !ctx.strategyConfig) return

        const params = buildScheduleParams(ctx.campaign, ctx.strategyType, ctx.strategyConfig, ctx.contentTypeSettings)
        createSchedules([params])
      },
    },
  })

  const [state, send] = machine
  sendRef.current = send

  const abortRef = useRef(state.context.transient.abortController)
  abortRef.current = state.context.transient.abortController

  useEffect(() => {
    return () => abortRef.current.abort()
  }, [])

  return machine
}
